import { EllipsisMenu, NodeStatus } from 'components/index';
import BaseTable from 'components/Tables/BaseTable';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { usePerms } from 'contexts/PermsContext';
import {
  selectDeviceTypeMap,
  selectSystemCount,
} from 'features/integrations/state/selectors/integrationSelectors';
import { deviceSearchFilter } from 'features/integrations/utils/integrationSearchUtils';
import { useMemo } from 'react';
import { Accordion, Button } from 'react-bootstrap';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa6';
import { useSelector } from 'react-redux';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useFusionGetIntegrationsQuery } from 'services/integrations/integrations.api';
import { deviceImageList, getDynamicDeviceIcon } from 'utils/iconUtils';
import {
  getfilteredConnectedDevices,
  getFilteredDeviceTypes,
} from '../utils/connectedSystemsUtils';
import { hasDevicePermissionFromDevice } from '../utils/devicePermissionUtils';

const Table = ({
  deviceIndexMap,
  filteredIntegrations,
  openEditFormForSystemOrDevice,
  prepareSystemOrDeviceForDeletion,
  deviceRowIdEllipsisOpen,
  setDeviceRowIdEllipsisOpen,
  systemRowIdEllipsisOpen,
  setSystemRowIdEllipsisOpen,
}) => {
  const perms = usePerms();

  const systemCount = useSelector(selectSystemCount);
  const { data: integrations = [] } = useFusionGetIntegrationsQuery();
  const deviceTypeMap = useSelector(selectDeviceTypeMap);
  const { data: connectedDevices = [], isFetching: connectedDevicesIsFetching } =
    useGetConnectedDevicesQuery();

  const columns = useMemo(() => {
    return [
      {
        header: 'Name',
        accessorKey: 'name',
        Cell: ({ row, cell }) => {
          const name = cell.getValue();
          const type = row.original.type;
          const email = row.original.email;

          return (
            <div className="d-flex gap-3 align-items-center">
              <div>
                {deviceImageList[type] ? (
                  <img src={deviceImageList[type]} alt={type} width={'30px'} />
                ) : (
                  <img src={deviceImageList['RESCALERT']} alt={'Rescalert'} width={'30px'} />
                )}
              </div>
              <div className="d-flex flex-column text-white fs-6">
                <div>{name}</div>
                <div className="text-break text-secondary">{email}</div>
              </div>
            </div>
          );
        },
      },
      {
        header: 'System Type',
        accessorKey: 'type',
        Cell: ({ cell }) => {
          const type = cell.getValue();
          return <div className="text-white fs-6">{type}</div>;
        },
      },
      {
        header: 'License Info',
        accessorKey: 'info',
        Cell: ({ cell }) => {
          const info = cell.getValue();
          return (
            <div className="text-secondary fs-6">
              {info}/{systemCount?.advanced_system || 0} (System)
            </div>
          );
        },
      },
      {
        header: 'STATUS',
        accessorKey: 'status',
        Cell: ({ cell }) => {
          const status = cell.getValue();
          const displayStatus = status === 1 ? 'Online' : 'Offline';
          return (
            <>
              {displayStatus === 'Online' ? (
                <div className="bg-success rounded-3 px-3 py-1 text-white fs-6 fst-italic">
                  {displayStatus}
                </div>
              ) : (
                <div className="bg-transparent border border-1 border-white rounded-3 px-3 py-1 text-white fs-6 fst-italic">
                  {displayStatus}
                </div>
              )}
            </>
          );
        },
      },
      {
        header: 'Date Integrated',
        accessorKey: 'date',
        Cell: ({ cell }) => {
          const date = cell.getValue();
          return <div className="text-white fs-6">{date}</div>;
        },
      },
      {
        header: '',
        accessorKey: 'actions',
        enableSorting: false,
        Cell: ({ row }) => {
          const rowId = row.original.id;
          const system = row.original.system;

          const canEditOrDelete =
            perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditIntegrations']) ||
            perms.testPermission([8, 9, 10, 11, 12, 13], ['DeleteIntegrations']);

          return (
            <div>
              {canEditOrDelete ? (
                <div
                  className="d-flex justify-content-center align-items-center gap-3 text-white"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <EllipsisMenu
                    options={[
                      {
                        component: (
                          <>
                            {perms.testPermission(
                              [8, 9, 10, 11, 12, 13],
                              ['Create-EditIntegrations'],
                            ) && (
                              <Button
                                className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                                onClick={() => openEditFormForSystemOrDevice('system', system)}
                                variant="secondary"
                              >
                                Edit
                              </Button>
                            )}
                          </>
                        ),
                      },
                      {
                        component: (
                          <>
                            {perms.testPermission(
                              [8, 9, 10, 11, 12, 13],
                              ['DeleteIntegrations'],
                            ) && (
                              <Button
                                className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                                onClick={() => prepareSystemOrDeviceForDeletion(system)}
                                variant="danger"
                              >
                                Remove
                              </Button>
                            )}
                          </>
                        ),
                      },
                    ]}
                    open={systemRowIdEllipsisOpen[rowId]}
                    containerClassname="bg-transparent"
                    onOpen={() =>
                      setSystemRowIdEllipsisOpen({
                        ...systemRowIdEllipsisOpen,
                        [rowId]: true,
                      })
                    }
                    onClose={() =>
                      setSystemRowIdEllipsisOpen({
                        ...systemRowIdEllipsisOpen,
                        [rowId]: false,
                      })
                    }
                  />

                  {row.getIsExpanded() ? <FaChevronUp /> : <FaChevronDown />}
                </div>
              ) : (
                <div />
              )}
            </div>
          );
        },
      },
    ];
  }, [
    perms,
    systemRowIdEllipsisOpen,
    systemCount,
    openEditFormForSystemOrDevice,
    prepareSystemOrDeviceForDeletion,
    setSystemRowIdEllipsisOpen,
  ]);

  const rows = useMemo(() => {
    if (!filteredIntegrations) return [];

    return filteredIntegrations.map((system, index) => {
      return {
        system,
        id: system.id || index,
        email: system.email || '',
        name: system.system_name || '',
        type: system.system_type || '',
        info: integrations?.length || 0,
        status: system.status,
        date: new Date(system.date_integration).toLocaleString(),
        filteredDeviceTypes: getFilteredDeviceTypes(system.system_type, deviceTypeMap, perms),
      };
    });
  }, [filteredIntegrations, integrations, deviceTypeMap, perms]);

  return (
    <>
      {!connectedDevicesIsFetching ? (
        <BaseTable
          columns={columns}
          rows={rows}
          expandable={true}
          customStyles={{
            cell: {
              borderBottom: '1px solid #56577A',
              cursor: 'pointer',
            },
            dropdown: { backgroundColor: '#222A2D' },
          }}
          dropdownMenu={(system) => (
            <Accordion>
              {system.filteredDeviceTypes &&
                Object.values(system.filteredDeviceTypes).map((deviceType, index) => {
                  return (
                    <Accordion.Item
                      eventKey={index}
                      key={index}
                      className="bordered-table-accordion-item"
                    >
                      <Accordion.Header className="d-flex align-items-center gap-2">
                        <img
                          src={deviceImageList['RESCALERT']}
                          alt={deviceType}
                          width={'30px'}
                          className="me-2"
                        />
                        {deviceType.replace('_', ' (')}
                        {deviceType.includes('_') ? ')' : ''}
                      </Accordion.Header>

                      <Accordion.Body>
                        {getfilteredConnectedDevices(
                          connectedDevices,
                          (device) => deviceSearchFilter(device),
                          deviceType,
                        ).map((device) => {
                          const hasDeletePermission = hasDevicePermissionFromDevice(
                            perms,
                            device,
                            'delete',
                          );
                          const hasCreateEditPermission = hasDevicePermissionFromDevice(
                            perms,
                            device,
                            'createEdit',
                          );

                          return (
                            <div
                              key={device.id}
                              className="d-flex align-items-center border-bottom border-1 border-primary p-2 mb-3"
                            >
                              <img
                                src={getDynamicDeviceIcon(device)}
                                alt={device?.system_type}
                                width={'30px'}
                                className="me-2"
                              />

                              <div className="flex-grow-1 flex-shrink-1 flex-basis-3xs me-4">
                                {`${device?.static_id} ${device?.alias}`}
                              </div>

                              <div className="flex-grow-1 flex-shrink-1 flex-basis-3xs me-4">
                                {`${device?.system_type} ${device?.device_type.replace('_', ' ')}`}
                              </div>

                              <div className="flex-grow-1 flex-shrink-1 flex-basis-3xs me-4">
                                {`${deviceIndexMap.get(device.id)}/${
                                  systemCount?.connected_devices
                                } (Device)`}
                              </div>

                              <div className="flex-grow-1 flex-shrink-1 flex-basis-3xs">
                                <NodeStatus device={device} />
                              </div>

                              <div className="flex-grow-1 flex-shrink-1 flex-basis-3xs">
                                {new Date(device?.date_created).toLocaleString()}
                              </div>

                              {hasCreateEditPermission || hasDeletePermission ? (
                                <div className="flex-grow-1 flex-shrink-1 flex-basis-3xs">
                                  <EllipsisMenu
                                    options={[
                                      hasCreateEditPermission && {
                                        component: (
                                          <Button
                                            className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                                            variant="secondary"
                                            onClick={() => {
                                              openEditFormForSystemOrDevice('device', device);
                                              setDeviceRowIdEllipsisOpen({
                                                ...deviceRowIdEllipsisOpen,
                                                [device.id]: false,
                                              });
                                            }}
                                          >
                                            Edit
                                          </Button>
                                        ),
                                      },
                                      hasDeletePermission && {
                                        component: (
                                          <Button
                                            className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                                            variant="danger"
                                            onClick={() => {
                                              prepareSystemOrDeviceForDeletion(device);
                                              setDeviceRowIdEllipsisOpen({
                                                ...deviceRowIdEllipsisOpen,
                                                [device.id]: false,
                                              });
                                            }}
                                          >
                                            Remove
                                          </Button>
                                        ),
                                      },
                                    ].filter(Boolean)}
                                    open={deviceRowIdEllipsisOpen[device.id]}
                                    containerClassname="bg-transparent"
                                    onOpen={() =>
                                      setDeviceRowIdEllipsisOpen({
                                        ...deviceRowIdEllipsisOpen,
                                        [device.id]: true,
                                      })
                                    }
                                    onClose={() =>
                                      setDeviceRowIdEllipsisOpen({
                                        ...deviceRowIdEllipsisOpen,
                                        [device.id]: false,
                                      })
                                    }
                                  />
                                </div>
                              ) : (
                                <div className="flex-grow-1 flex-shrink-1 flex-basis-3xs" />
                              )}
                            </div>
                          );
                        })}
                      </Accordion.Body>
                    </Accordion.Item>
                  );
                })}
            </Accordion>
          )}
        />
      ) : (
        <WidgetLoadingAnimation />
      )}
    </>
  );
};

export default Table;
